import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllSeries, clearAllSeries } from "./../../actions/seriesActions";
import { useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
  TableSortLabel,
} from "@mui/material";
import { Edit, Collections } from '@mui/icons-material';
import LoadingBackdrop from "../Common/LoadingBackdrop";

const SeriesTable = ({ searchText }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Sort configuration state
  const [sortConfig, setSortConfig] = useState({
    key: 'series_created_at',
    direction: 'desc' // Default to descending (newest first)
  });

  const series = useSelector((state) => state.series.all_series);
  const loading = useSelector((state) => state.series.isLoading);

  useEffect(() => {
    dispatch(clearAllSeries());
    dispatch(fetchAllSeries());
  }, [dispatch]);

  const handleEdit = (series) => {
    navigate(`/series/edit/${series.series_id}`);
  };

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Enhanced sorting function
  const sortData = (data) => {
    if (!data || !sortConfig.key) return data || [];
    
    return [...data].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle null/undefined values first to ensure consistent normalization
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      
      // Handle different data types consistently
      if (sortConfig.key === 'series_created_at') {
        // For dates, treat empty/null as epoch 0
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      } else if (sortConfig.key === 'series_is_active') {
        // For status/boolean fields, ensure numeric comparison
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        // For strings (title, category, etc.), normalize to lowercase
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      
      let comparison = 0;
      if (aVal > bVal) comparison = 1;
      if (aVal < bVal) comparison = -1;
      
      return sortConfig.direction === 'desc' ? comparison * -1 : comparison;
    });
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Sort label helper - TableSortLabel expects 'asc'/'desc' or undefined, not false
  const getSortDirection = (column) => {
    return sortConfig.key === column ? sortConfig.direction : undefined;
  };

  const renderSeries = () => {
    if (!series) return null;

    let data = [...series];
    if (searchText) {
      const search = searchText.toLowerCase();
      data = data.filter(
        (obj) => obj.series_title.toLowerCase().includes(search) ||
          (obj.category_title || '').toLowerCase().includes(search)
      );
    }

    data = sortData(data);

    if (data.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
            <Typography variant="subtitle1" color="text.secondary">
              {searchText ? "No series found matching your search" : "No series available"}
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    return data.map((obj) => (
      <TableRow
        key={obj.series_id + obj.series_title}
        sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
      >
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1">{obj.series_title}</Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="subtitle1">{obj.category_title || "None"}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {formatDate(obj.series_created_at)}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography
            sx={{
              color: obj.series_is_active ? 'green' : 'red',
              fontWeight: 500,
            }}
          >
            {obj.series_is_active ? 'Active' : 'Inactive'}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Tooltip title="Edit">
            <IconButton
              color="info"
              onClick={() => handleEdit(obj)}
              sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
            >
              <Edit />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    ));
  };

  if (loading && !series) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      <LoadingBackdrop open={loading && series !== null} />
      <TableContainer
        component={Paper}
        sx={{
          marginTop: 3,
          boxShadow: 2,
          borderRadius: 1,
          overflow: 'hidden'
        }}
      >
        <Table aria-label="series table">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <TableSortLabel
                  active={sortConfig.key === 'series_title'}
                  direction={getSortDirection('series_title')}
                  onClick={() => handleSort('series_title')}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <TableSortLabel
                  active={sortConfig.key === 'category_title'}
                  direction={getSortDirection('category_title')}
                  onClick={() => handleSort('category_title')}
                >
                  Category
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <TableSortLabel
                  active={sortConfig.key === 'series_created_at'}
                  direction={getSortDirection('series_created_at')}
                  onClick={() => handleSort('series_created_at')}
                >
                  Created Date
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <TableSortLabel
                  active={sortConfig.key === 'series_is_active'}
                  direction={getSortDirection('series_is_active')}
                  onClick={() => handleSort('series_is_active')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {renderSeries()}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SeriesTable;
